import * as React from 'react'

import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import {
    UserLanguageSelectMenu,
    UserPaletteSelectMenu,
    UserBackgroundSelectMenu,
    UserNodeLabelsThresholdSelectMenu,
    UserNodeLabelsSizeSelectMenu,
    UserEdgesTypeSelectMenu,
    UserAbstractViewSelectMenu,
    UserBigramsSelectMenu,
    UserPhrasesSelectMenu,
    UserInfluentialNodesSelectMenu,
    UserNumberOfNodesSelectMenu,
    UserHashtagsSelectMenu,
    UserHashtagMorphemesSelectMenu,
    UserSquarebracketsSelectMenu,
    UserWikilinksSelectMenu,
    UserMentionsSelectMenu,
    UserStopwordsMenu,
    UserSynonymsMenu,
    UserVoiceSelectMenu,
    UserBrandingMenu,
    UserMidiMenu,
} from './SettingsTemplates'

import { useTheme } from '@mui/material/styles'

import { UserContext } from '../UserContext'
import { appProductName } from '../appVariant'
import FilterChips from '../components/FilterChips/Settings'
import Button from '@mui/material/Button'

import useMediaQuery from '@mui/material/useMediaQuery'

import axios from 'axios'
import qs from 'qs'

import { useSnackbar } from '../components/Snackbar'

import { sendToAnalytics } from '../Analytics'
import PasswordBlock from '../components/AccountPassword/PasswordBlock'
import EmailPreferencesBlock from '../components/AccountEmail/EmailPreferencesBlock'

const selectorShowSnackbar = state => state.showSnackbar

const defaultMidi = {
    midiactive: 'off',
    midinodechannel: '01',
    midiedgechannel: '02',
    mididevice: '00',
    mididuration: '4000',
    midinodenote: 'C2',
    midiedgenote: 'C2',
    midiinput: 'no',
}

export default function Settings(props) {
    const {
        id,
        username,
        email,
        plan,
        status,
        squarebrackets,
        topnodes,
        label_size,
        customization,
        hashnodes,
        morphemes,
        midi,
        label_threshold,
        palette,
        edge_type,
        stopwords,
        inlanguage,
        synonyms,
        maxnodes,
        abstract,
        stopwordsoverride,
        background,
        mentions,
        wikilinks,
        phrases,
        fullview,
        voice_continues,
    } = React.useContext(UserContext)

    const [userLanguage, setUserLanguage] = React.useState(
        inlanguage ? inlanguage : 'auto'
    )

    const [userPalette, setUserPalette] = React.useState(
        palette ? palette : 'default'
    )

    const [userBackground, setUserBackground] = React.useState(
        background ? background : 'default'
    )

    const [
        userNodeLabelsThreshold,
        setUserNodeLabelsThreshold,
    ] = React.useState(label_threshold ? label_threshold : '8')

    const [userNodeLabelsSize, setUserNodeLabelsSize] = React.useState(
        label_size ? label_size : 'proportional'
    )

    const [userEdgesType, setUserEdgesType] = React.useState(
        edge_type ? edge_type : 'line'
    )

    const [userAbstractView, setUserAbstractView] = React.useState(
        abstract ? abstract : 'no'
    )

    const [userBigrams, setUserBigrams] = React.useState(
        fullview || fullview == '0' ? fullview : '1'
    )

    const [userPhrases, setUserPhrases] = React.useState(
        phrases || phrases == '0' ? phrases : '1'
    )

    const [userNumberOfNodes, setUserNumberOfNodes] = React.useState(
        maxnodes ? maxnodes : '150'
    )

    const [userInfluentialNodes, setUserInfluentialNodes] = React.useState(
        topnodes && topnodes == 'degree' ? 'degree' : 'bc'
    )

    const [userHashtags, setUserHashtags] = React.useState(
        hashnodes ? hashnodes : '0'
    )

    const [userHashtagMorphemes, setUserHashtagMorphemes] = React.useState(
        morphemes ? morphemes : '0'
    )

    const [userSquarebrackets, setUserSquarebrackets] = React.useState(
        squarebrackets ? squarebrackets : '0'
    )

    const [userWikilinks, setUserWikilinks] = React.useState(
        wikilinks ? wikilinks : 'entities'
    )

    const [userMentions, setUserMentions] = React.useState(
        mentions ? mentions : '0'
    )

    const [userStopwords, setUserStopwords] = React.useState(
        stopwords ? stopwords : ''
    )

    const [userStopwordsOverride, setUserStopwordsOverride] = React.useState(
        stopwordsoverride ? stopwordsoverride : '0'
    )

    const [userSynonyms, setUserSynonyms] = React.useState(
        synonyms ? synonyms : ''
    )

    const [userVoicePhrase, setUserVoicePhrase] = React.useState(
        voice_continues ? voice_continues : '00'
    )

    const [userBrandingLabel, setUserBrandingLabel] = React.useState(
        customization ? customization : appProductName()
    )

    const [userMidi, setUserMidi] = React.useState({})

    React.useEffect(() => {
        setUserLanguage(inlanguage ? inlanguage : 'auto')
    }, [inlanguage])

    React.useEffect(() => {
        setUserPalette(palette)
    }, [palette])

    React.useEffect(() => {
        setUserBackground(background)
    }, [background])

    React.useEffect(() => {
        setUserNodeLabelsThreshold(label_threshold)
    }, [label_threshold])

    React.useEffect(() => {
        setUserNodeLabelsSize(label_size ? label_size : 'proportional')
    }, [label_size])

    React.useEffect(() => {
        setUserEdgesType(edge_type ? edge_type : 'line')
    }, [edge_type])

    React.useEffect(() => {
        setUserAbstractView(abstract ? abstract : 'no')
    }, [abstract])

    React.useEffect(() => {
        setUserBigrams(fullview)
    }, [fullview])

    React.useEffect(() => {
        setUserPhrases(phrases)
    }, [phrases])

    React.useEffect(() => {
        setUserNumberOfNodes(maxnodes ? maxnodes : 150)
    }, [maxnodes])

    React.useEffect(() => {
        setUserInfluentialNodes(
            topnodes && topnodes == 'degree' ? 'degree' : 'bc'
        )
    }, [topnodes])

    React.useEffect(() => {
        setUserHashtags(hashnodes)
    }, [hashnodes])

    React.useEffect(() => {
        setUserHashtagMorphemes(morphemes)
    }, [morphemes])

    React.useEffect(() => {
        setUserSquarebrackets(squarebrackets ? squarebrackets : '0')
    }, [squarebrackets])

    React.useEffect(() => {
        setUserWikilinks(wikilinks ? wikilinks : 'entities')
    }, [wikilinks])

    React.useEffect(() => {
        setUserMentions(mentions)
    }, [mentions])

    React.useEffect(() => {
        setUserStopwords(stopwords ? stopwords : '')
    }, [stopwords])

    React.useEffect(() => {
        setUserStopwordsOverride(stopwordsoverride ? stopwordsoverride : '')
    }, [stopwordsoverride])

    React.useEffect(() => {
        setUserSynonyms(synonyms ? synonyms : '')
    }, [synonyms])

    React.useEffect(() => {
        setUserVoicePhrase(voice_continues ? voice_continues : '00')
    }, [voice_continues])

    React.useEffect(() => {
        setUserBrandingLabel(customization ? customization : appProductName())
    }, [customization])

    React.useEffect(() => {
        setUserMidi(convertOldMidiToNew(midi))
    }, [midi])

    const [selectedCategory, setSelectedCategory] = React.useState('language')

    const isMobile = useMediaQuery('(max-width:600px)')

    React.useEffect(() => {}, [selectedCategory])

    const showSnackbar = useSnackbar(selectorShowSnackbar)

    const saveLanguageSettings = () => {
        const dataToSend = {
            inlanguage: userLanguage,
        }

        const dataAsForm = qs.stringify(dataToSend)

        postSettings(dataAsForm, `Language changed to ${userLanguage}.`)

        sendToAnalytics({
            userId: username,
            event: 'change_user_settings',
            data: { type: 'language_settings' },
        })
    }

    const saveDisplaySettings = () => {
        const dataToSend = {
            palette: userPalette,
            background: userBackground,
            label_threshold: userNodeLabelsThreshold,
            label_size: userNodeLabelsSize,
            abstract: userAbstractView,
            edge_type: userEdgesType,
            phrases: userPhrases,
        }

        const dataAsForm = qs.stringify(dataToSend)

        postSettings(
            dataAsForm,
            `Background ${userBackground}, Palette: ${userPalette}, Label Threshold: ${userNodeLabelsThreshold}, Label Size: ${userNodeLabelsSize}, Abstract ${userAbstractView}, Edge Type: ${userEdgesType}, Phrases: ${userPhrases}.}`
        )

        sendToAnalytics({
            userId: username,
            event: 'change_user_settings',
            data: { type: 'display_settings' },
        })
    }

    const saveGraphSettings = () => {
        const dataToSend = {
            fullview: userBigrams,
            maxnodes: userNumberOfNodes,
            topnodes: userInfluentialNodes,
        }

        const dataAsForm = qs.stringify(dataToSend)

        postSettings(
            dataAsForm,
            `Graph Connections: ${userBigrams}, Maximum Nodes: ${userNumberOfNodes}, Influential Nodes: ${userInfluentialNodes}.`
        )

        sendToAnalytics({
            userId: username,
            event: 'change_user_settings',
            data: { type: 'graph_settings' },
        })
    }

    const saveTextSettings = () => {
        const dataToSend = {
            hashnodes: userHashtags,
            morphemes: userHashtagMorphemes,
            squarebrackets: userSquarebrackets,
            wikilinks: userWikilinks,
        }

        const dataAsForm = qs.stringify(dataToSend)

        postSettings(dataAsForm, 'Updated text processing settings.')

        sendToAnalytics({
            userId: username,
            event: 'change_user_settings',
            data: { type: 'text_processing_settings' },
        })
    }

    const saveStopwordsSettings = () => {
        const dataToSend = {
            stopwords: userStopwords ? userStopwords : ' ',
            stopwordsoverride: userStopwordsOverride,
        }

        const dataAsForm = qs.stringify(dataToSend)

        postSettings(dataAsForm, 'Updated stopwords settings.')

        sendToAnalytics({
            userId: username,
            event: 'change_user_settings',
            data: { type: 'stopwords_settings' },
        })
    }

    const saveSynonymsSettings = () => {
        const dataToSend = {
            synonyms: userSynonyms ? userSynonyms : ' ',
        }

        const dataAsForm = qs.stringify(dataToSend)

        postSettings(dataAsForm, 'Updated synonyms.')

        sendToAnalytics({
            userId: username,
            event: 'change_user_settings',
            data: { type: 'synonyms_settings' },
        })
    }

    const saveVoiceSettings = () => {
        const dataToSend = {
            voice_continues: userVoicePhrase,
        }

        const dataAsForm = qs.stringify(dataToSend)

        postSettings(dataAsForm, 'Updated voice settings.')

        sendToAnalytics({
            userId: username,
            event: 'change_user_settings',
            data: { type: 'voice_settings' },
        })
    }

    const saveBrandingSettings = () => {
        const dataToSend = {
            customization: userBrandingLabel,
        }

        const dataAsForm = qs.stringify(dataToSend)

        postSettings(
            dataAsForm,
            `Updated custom branding to ${userBrandingLabel}.`
        )

        sendToAnalytics({
            userId: username,
            event: 'change_user_settings',
            data: { type: 'customization_branding_settings' },
        })
    }

    const saveMidiSettings = () => {
        const dataToSend = {
            midiactive: userMidi.midiactive,
            midinodechannel: userMidi.midinodechannel,
            midiedgechannel: userMidi.midiedgechannel,
            mididevice: userMidi.mididevice,
            mididuration: userMidi.mididuration,
            midinodenote: userMidi.midinodenote,
            midiedgenote: userMidi.midiedgenote,
            midiinput: userMidi.midiinput,
        }

        const dataAsForm = qs.stringify(dataToSend)

        postSettings(
            dataAsForm,
            `Updated MIDI settings (MIDI is ${dataToSend.midiactive}).`
        )

        sendToAnalytics({
            userId: username,
            event: 'change_user_settings',
            data: { type: 'midi_settings' },
        })
    }
    const postSettings = (submittedData, successMessage) => {
        if (!successMessage) successMessage = ''

        axios
            .post('/settings', submittedData, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded', // Sending as URL-encoded form data
                },
            })
            .then(response => {
                if (response.data.success) {
                    showSnackbar(
                        'success',
                        `${response.data.success} ${successMessage}`
                    )
                } else {
                    console.error(`Did't receive any response from the server.`)
                    if (response.data.error) {
                        showSnackbar('error', response.data.error)
                    }
                }
            })
            .catch(error => {
                // Handle the error here
                console.error('Received the following error', error)
                showSnackbar('error', JSON.stringify(error))
            })
    }

    return (
        <>
            <Container
                component="main"
                sx={{
                    backgroundColor: theme => theme.palette.background.default,
                    flexGrow: 1,
                    height: '100vh',
                    overflow: 'auto',
                    ml: 5,
                    pl: 0,
                }}
            >
                <Box
                    sx={{
                        pt: 8,
                        pb: 2,
                        pl: 0,
                        ml: 0,
                        maxWidth: '800px',
                    }}
                >
                    <Typography
                        component="span"
                        variant="subtitle1"
                        align="left"
                        color="text.primary"
                    >
                        Adjust text processing and graph display settings for
                        the user{' '}
                        <Typography component="span" color="text.secondary">
                            {username}
                        </Typography>{' '}
                        on the global level
                    </Typography>
                </Box>
                <Divider
                    variant="middle"
                    textAlign="left"
                    sx={{ ml: 0, marginBottom: 2, maxWidth: '800px' }}
                />
                <PasswordBlock />
                <EmailPreferencesBlock />
                <Box sx={{ ml: 0, pl: 0, maxWidth: '800px' }}>
                    <Grid container sx={{ ml: 0, pl: 0 }} spacing={0}>
                        <Grid item md={12} xs={12} sx={{ ml: 0, pl: 0 }}>
                            <FilterChips
                                preselectedChip={selectedCategory}
                                onClick={setSelectedCategory}
                            />
                        </Grid>
                        <Grid item sx={{ mt: 2, ml: 0 }} md={12} xs={12}>
                            {selectedCategory == 'language' && (
                                <>
                                    <Typography
                                        component="h6"
                                        variant="h6"
                                        align="left"
                                        color="text.primary"
                                        sx={{ mb: 2 }}
                                    >
                                        Language Settings:
                                    </Typography>
                                    <Grid container spacing={8}>
                                        <Grid item md={7} xs={12}>
                                            <Typography variant="subtitle1">
                                                Text input language,
                                                lemmatization, and stemming
                                            </Typography>

                                            <UserLanguageSelectMenu
                                                userLanguage={userLanguage}
                                                setUserLanguage={
                                                    setUserLanguage
                                                }
                                            />

                                            <Button
                                                variant="contained"
                                                onClick={saveLanguageSettings}
                                                sx={{ mt: 4, mb: 8 }}
                                            >
                                                Save Language Settings
                                            </Button>
                                        </Grid>
                                        <Grid
                                            item
                                            md={4}
                                            xs={12}
                                            sx={{ mb: 4 }}
                                        >
                                            <Typography
                                                component="span"
                                                variant="caption"
                                            >
                                                We will convert your words into
                                                lemmas (e.g.: taken &#8594;
                                                take, apples &#8594; apple)
                                                based on this language
                                                selection. <br />
                                                This setting will also apply to
                                                stopwords removal and
                                                voice-to-text detection. You can
                                                also choose to switch
                                                auto-detection off to avoid
                                                lemmatization and add your own{' '}
                                                <Link
                                                    onClick={() => {
                                                        setSelectedCategory(
                                                            'stopwords'
                                                        )
                                                    }}
                                                    style={{
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    list of stopwords
                                                </Link>{' '}
                                                to process the languages we do
                                                not support.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                            {selectedCategory == 'display' && (
                                <>
                                    <Typography
                                        component="h6"
                                        variant="h6"
                                        align="left"
                                        color="text.primary"
                                    >
                                        Graph Display Settings:
                                    </Typography>
                                    <Grid container spacing={8}>
                                        <Grid item md={7} xs={12}>
                                            <Box sx={{ mt: 1 }}>
                                                <UserBackgroundSelectMenu
                                                    userBackground={
                                                        userBackground
                                                    }
                                                    setUserBackground={
                                                        setUserBackground
                                                    }
                                                />

                                                <UserPaletteSelectMenu
                                                    userPalette={userPalette}
                                                    setUserPalette={
                                                        setUserPalette
                                                    }
                                                />

                                                <UserNodeLabelsThresholdSelectMenu
                                                    userNodeLabelsThreshold={
                                                        userNodeLabelsThreshold
                                                    }
                                                    setUserNodeLabelsThreshold={
                                                        setUserNodeLabelsThreshold
                                                    }
                                                />

                                                <UserNodeLabelsSizeSelectMenu
                                                    userNodeLabelsSize={
                                                        userNodeLabelsSize
                                                    }
                                                    setUserNodeLabelsSize={
                                                        setUserNodeLabelsSize
                                                    }
                                                />

                                                <UserEdgesTypeSelectMenu
                                                    userEdgesType={
                                                        userEdgesType
                                                    }
                                                    setUserEdgesType={
                                                        setUserEdgesType
                                                    }
                                                />

                                                <UserAbstractViewSelectMenu
                                                    userAbstractView={
                                                        userAbstractView
                                                    }
                                                    setUserAbstractView={
                                                        setUserAbstractView
                                                    }
                                                />

                                                <UserPhrasesSelectMenu
                                                    userPhrases={userPhrases}
                                                    setUserPhrases={
                                                        setUserPhrases
                                                    }
                                                />
                                            </Box>
                                            <Button
                                                variant="contained"
                                                onClick={saveDisplaySettings}
                                                sx={{ mt: 4, mb: 8 }}
                                            >
                                                Save Graph Display Settings
                                            </Button>
                                        </Grid>
                                        <Grid item md={4} xs={12}>
                                            <Box sx={{ mt: 4, mb: 4 }}>
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                >
                                                    You can make your graphs as
                                                    specific and precise or as
                                                    abstract and beautiful as
                                                    you wish. These settings
                                                    will also be used when
                                                    showing your public graphs.
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                            {selectedCategory == 'graph' && (
                                <>
                                    <Typography
                                        component="h6"
                                        variant="h6"
                                        align="left"
                                        color="text.primary"
                                    >
                                        Graph Processing Settings:
                                    </Typography>
                                    <Grid container spacing={8}>
                                        <Grid item md={7} xs={12}>
                                            <Box sx={{ mt: 1 }}>
                                                <UserBigramsSelectMenu
                                                    userBigrams={userBigrams}
                                                    setUserBigrams={
                                                        setUserBigrams
                                                    }
                                                />

                                                <UserNumberOfNodesSelectMenu
                                                    userNumberOfNodes={
                                                        userNumberOfNodes
                                                    }
                                                    setUserNumberOfNodes={
                                                        setUserNumberOfNodes
                                                    }
                                                />

                                                <UserInfluentialNodesSelectMenu
                                                    userInfluentialNodes={
                                                        userInfluentialNodes
                                                    }
                                                    setUserInfluentialNodes={
                                                        setUserInfluentialNodes
                                                    }
                                                />
                                            </Box>
                                            <Button
                                                variant="contained"
                                                onClick={saveGraphSettings}
                                                sx={{ mt: 4, mb: 8 }}
                                            >
                                                Save Graph Processing Settings
                                            </Button>
                                        </Grid>
                                        <Grid item md={4} xs={12}>
                                            <Box sx={{ mt: 4, mb: 4 }}>
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                >
                                                    We recommed you to change
                                                    these parameters only if you
                                                    are familiar with graph
                                                    theory and network analysis.
                                                    The default values are
                                                    optimal for topic modeling
                                                    and guarantee the best
                                                    results.
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                            {selectedCategory == 'processing' && (
                                <>
                                    <Typography
                                        component="h6"
                                        variant="h6"
                                        align="left"
                                        color="text.primary"
                                    >
                                        Text Processing Settings:
                                    </Typography>
                                    <Grid container spacing={8}>
                                        <Grid item md={7} xs={12}>
                                            <Box sx={{ mt: 1 }}>
                                                <UserHashtagsSelectMenu
                                                    userHashtags={userHashtags}
                                                    setUserHashtags={
                                                        setUserHashtags
                                                    }
                                                />
                                                <UserHashtagMorphemesSelectMenu
                                                    userHashtagMorphemes={
                                                        userHashtagMorphemes
                                                    }
                                                    setUserHashtagMorphemes={
                                                        setUserHashtagMorphemes
                                                    }
                                                />
                                                <UserSquarebracketsSelectMenu
                                                    userSquarebrackets={
                                                        userSquarebrackets
                                                    }
                                                    setUserSquarebrackets={
                                                        setUserSquarebrackets
                                                    }
                                                />
                                                <UserWikilinksSelectMenu
                                                    userWikilinks={
                                                        userWikilinks
                                                    }
                                                    setUserWikilinks={
                                                        setUserWikilinks
                                                    }
                                                />
                                                <UserMentionsSelectMenu
                                                    userMentions={userMentions}
                                                    setUserMentions={
                                                        setUserMentions
                                                    }
                                                />
                                            </Box>
                                            <Button
                                                variant="contained"
                                                onClick={saveTextSettings}
                                                sx={{ mt: 4, mb: 8 }}
                                            >
                                                Save Text Processing Settings
                                            </Button>
                                        </Grid>
                                        <Grid item md={4} xs={12}>
                                            <Box sx={{ mt: 4, mb: 4 }}>
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                >
                                                    These settings determine how
                                                    your text data will be
                                                    converted into a network
                                                    graph. Please, change these
                                                    parameters only if you know
                                                    what you're doing.
                                                    Otherwise, you can simply
                                                    use different{' '}
                                                    <a href="/apps">
                                                        editor apps
                                                    </a>{' '}
                                                    inside InfraNodus to change
                                                    the way your text is
                                                    processed. For instance, the
                                                    Mind Mapping app will use
                                                    different parameters than
                                                    the ones selected here.
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                            {selectedCategory == 'stopwords' && (
                                <>
                                    <Typography
                                        component="h6"
                                        variant="h6"
                                        align="left"
                                        color="text.primary"
                                    >
                                        Stopwords Settings:
                                    </Typography>
                                    <Grid container spacing={8}>
                                        <Grid item md={7} xs={12}>
                                            <UserStopwordsMenu
                                                userStopwords={userStopwords}
                                                setUserStopwords={
                                                    setUserStopwords
                                                }
                                                userStopwordsOverride={
                                                    userStopwordsOverride
                                                }
                                                setUserStopwordsOverride={
                                                    setUserStopwordsOverride
                                                }
                                            />

                                            <Button
                                                variant="contained"
                                                onClick={saveStopwordsSettings}
                                                sx={{ mt: 4, mb: 8 }}
                                            >
                                                Save Stopwords Settings
                                            </Button>
                                        </Grid>
                                        <Grid
                                            item
                                            md={4}
                                            xs={12}
                                            sx={{ mb: 4 }}
                                        >
                                            <Box sx={{ mt: 4, mb: 4 }}>
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                >
                                                    Comma-separated custom
                                                    stopwords to add to the
                                                    default list (
                                                    <a href="/files/stopwords_en_en.txt">
                                                        en
                                                    </a>
                                                    ,{' '}
                                                    <a href="/files/stopwords_de_de.txt">
                                                        de
                                                    </a>
                                                    ,{' '}
                                                    <a href="/files/stopwords_fr_fr.txt">
                                                        fr
                                                    </a>
                                                    ,{' '}
                                                    <a href="/files/stopwords_es_es.txt">
                                                        es
                                                    </a>
                                                    ,{' '}
                                                    <a href="/files/stopwords_it_it.txt">
                                                        it
                                                    </a>
                                                    ,{' '}
                                                    <a href="/files/stopwords_nl_nl.txt">
                                                        nl
                                                    </a>
                                                    ,{' '}
                                                    <a href="/files/stopwords_pt_pt.txt">
                                                        pt
                                                    </a>
                                                    ,{' '}
                                                    <a href="/files/stopwords_ru_ru.txt">
                                                        ru
                                                    </a>
                                                    ,{' '}
                                                    <a href="/files/stopwords_sv_sv.txt">
                                                        sv
                                                    </a>
                                                    )
                                                    <br />
                                                    Example: "alt, don, the"
                                                    will remove these words for
                                                    every language selection.{' '}
                                                    <br />
                                                    <br />
                                                    To exclude the words from
                                                    the default stopwords list,
                                                    use a "-" sign in front,
                                                    e.g. "-kind, -and, -the"
                                                    will keep them in the graph
                                                    and make sure they are not
                                                    changed into lemmas.
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                            {selectedCategory == 'synonyms' && (
                                <>
                                    <Typography
                                        component="h6"
                                        variant="h6"
                                        align="left"
                                        color="text.primary"
                                    >
                                        Synonyms Settings:
                                    </Typography>
                                    <Grid container spacing={8}>
                                        <Grid item md={7} xs={12}>
                                            <UserSynonymsMenu
                                                userSynonyms={userSynonyms}
                                                setUserSynonyms={
                                                    setUserSynonyms
                                                }
                                            />

                                            <Button
                                                variant="contained"
                                                onClick={saveSynonymsSettings}
                                                sx={{ mt: 4, mb: 8 }}
                                            >
                                                Save Synonyms Settings
                                            </Button>
                                        </Grid>
                                        <Grid
                                            item
                                            md={4}
                                            xs={12}
                                            sx={{ mb: 4 }}
                                        >
                                            <Box sx={{ mt: 4, mb: 4 }}>
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                >
                                                    You can change the way
                                                    certain words appear on the
                                                    graph through synonyms
                                                    feature. For example, you
                                                    can set "artificial" and
                                                    "intelligence" to be
                                                    referred to to "artificial
                                                    intelligence". Or you can
                                                    change the way a certain
                                                    word is written (e.g. "idea"
                                                    becomes "concept"). Learn
                                                    more on our{' '}
                                                    <a href="https://support.noduslabs.com/hc/en-us/articles/360014584539-How-to-Merge-Nodes-into-Topics-and-Unlock-Merged-Nodes">
                                                        support portal
                                                    </a>
                                                    .
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </>
                            )}

                            {selectedCategory == 'voice' && (
                                <>
                                    <Typography
                                        component="h6"
                                        variant="h6"
                                        align="left"
                                        color="text.primary"
                                    >
                                        Voice-to-Text Input:
                                    </Typography>
                                    <Grid container spacing={8}>
                                        <Grid item md={7} xs={12}>
                                            <Box sx={{ mt: 1 }}></Box>
                                            <Button
                                                variant="contained"
                                                onClick={saveVoiceSettings}
                                                sx={{ mt: 4, mb: 8 }}
                                            >
                                                Save Voice-to-Text Settings
                                            </Button>
                                        </Grid>
                                        <Grid item md={4} xs={12}>
                                            <Box sx={{ mt: 4, mb: 4 }}>
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                >
                                                    Works with Google Chrome
                                                    only at the moment. You can
                                                    choose whether to add your
                                                    words when you pause
                                                    (automatically), when you
                                                    click Save, or say a certain
                                                    word. Contact us to add more
                                                    options.
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                            {selectedCategory == 'branding' && (
                                <>
                                    <Typography
                                        component="h6"
                                        variant="h6"
                                        align="left"
                                        color="text.primary"
                                    >
                                        Custom Branding Settings:
                                    </Typography>
                                    <Grid container spacing={8}>
                                        <Grid item md={7} xs={12}>
                                            <Box sx={{ mt: 1 }}>
                                                <UserBrandingMenu
                                                    userBrandingLabel={
                                                        userBrandingLabel
                                                    }
                                                    setUserBrandingLabel={
                                                        setUserBrandingLabel
                                                    }
                                                />
                                            </Box>
                                            <Button
                                                variant="contained"
                                                onClick={saveBrandingSettings}
                                                sx={{ mt: 4, mb: 8 }}
                                            >
                                                Save Custom Branding Settings
                                            </Button>
                                        </Grid>
                                        <Grid item md={4} xs={12}>
                                            <Box sx={{ mt: 4, mb: 4 }}>
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                >
                                                    By default, the top right
                                                    part of the graph displays
                                                    InfraNodus branding, however
                                                    you can change it to the
                                                    name of your company or a
                                                    custom label. This feature
                                                    is only available to the{' '}
                                                    <a href="/subscription">
                                                        premium
                                                    </a>{' '}
                                                    users only.
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                            {selectedCategory == 'midi' && (
                                <>
                                    <Typography
                                        component="h6"
                                        variant="h6"
                                        align="left"
                                        color="text.primary"
                                    >
                                        MIDI Settings:
                                    </Typography>
                                    <Grid container spacing={8}>
                                        <Grid item md={7} xs={12}>
                                            <Box sx={{ mt: 1 }}>
                                                <UserMidiMenu
                                                    userMidi={userMidi}
                                                    setUserMidi={setUserMidi}
                                                />
                                            </Box>
                                            <Button
                                                variant="contained"
                                                onClick={saveMidiSettings}
                                                sx={{ mt: 4, mb: 8 }}
                                            >
                                                Save MIDI Settings
                                            </Button>
                                        </Grid>
                                        <Grid item md={4} xs={12}>
                                            <Box sx={{ mt: 4, mb: 4 }}>
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                >
                                                    You can receive the data for
                                                    the graph using a MIDI
                                                    interface to visualize and
                                                    analyze melodies, rhythms,
                                                    and notes (e.g. C2 D2 E1)
                                                    etc. You can also use your
                                                    interactions with the graph
                                                    to play MIDI notes on an
                                                    external device. Read more
                                                    about it on our{' '}
                                                    <a href="https://support.noduslabs.com/hc/en-us/articles/360014898719-Making-Music-with-Network-Graphs-Auditory-Feedback-on-Your-Research-Process-via-MIDI">
                                                        support portal
                                                    </a>
                                                    .
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Box>
            </Container>
        </>
    )
}

function convertOldMidiToNew(userMidiString) {
    if (!userMidiString || userMidiString === 'off') {
        return defaultMidi
    }

    return {
        midiactive: userMidiString.length > 3 ? 'on' : 'off',
        midinodechannel: userMidiString.substr(0, 2),
        midiedgechannel: userMidiString.substr(2, 2),
        mididevice: userMidiString.substr(4, 2),
        mididuration: userMidiString.substr(6, 4),
        midinodenote: userMidiString.substr(10, 2) || 'C2',
        midiedgenote: userMidiString.substr(12, 2) || 'C2',
        midiinput: userMidiString.substr(14, 2) || 'no',
    }
}
